/*
 * Copyright (c) Nexign, JSC, 2021.
 */
/**
 * Page Cashback History tab component
 *
 * @author Yakov Gushchin
 * @author Julia Ignatieva
 * @version 1.14.0
 * @since 1.12.0
 */

import { useCallback } from 'react';

import { connect } from 'react-redux';
import { Dispatch } from 'redux';
import { createStructuredSelector } from 'reselect';

import { renderDropFilterButton } from '../../../../components/common/PageTitle/PageTitle';
import { useTable } from '../../../../components/Table/hooks/useTable';
import { TableNameType } from '../../../../components/Table/types/tableTypes';
import { pushGTMAnalytic } from '../../../../modules/analytics/GoogleTagManager/GTMActions';
import GTMOperations from '../../../../modules/analytics/GoogleTagManager/GTMOperations';
import { IGTMPushAnalyticPayload } from '../../../../modules/analytics/GoogleTagManager/types/GTMActionTypes';
import {
    makeSelectPageCashbackTranslation,
    makeSelectTranslationCommon,
} from '../../../../modules/internationalization/intSelectors';
import { ICommonTranslation } from '../../../../modules/internationalization/types';
import { IPageCashbackTranslation } from '../../../../modules/internationalization/types/pages/cashback';
import { IRootReducer } from '../../../../reducers/rootReducer';
import { fonts } from '../../../../theme';
import declination from '../../../../utils/declination';
import { formatBalanceWithoutTrailingZeros } from '../../../../utils/formatText';
import { IPageCashbackReducer } from '../../pageCashbackReducer';
import { makeSelectCashbackMeta, makeSelectCashbackSelectCurrent } from '../../selectors/cashbackSelectors';
import CashbackNotAvailable from '../CashbackNotAvailable';
import { CashbackHistoryStyled } from '../styled/CashbackHistoryStyled';
import { CashbackTextStyled } from '../styled/CashbackStyled';
import CashbackHistoryTable from './CashbackHistoryTable';

const CashbackHistory = (props: IConnectedProps & IDispatchProps) => {

    const {
        accountSelectCurrent,
        meta,
        pushGtmAnalytic,
        translationCashback,
        translationCommon,
    } = props;

    const {
        selectedFilters,

        dropFilterAndSort,
    } = useTable(TableNameType.cashbackHistory);

    const onLinkClick = useCallback((link: string) => {
        pushGtmAnalytic({
            operation: GTMOperations.cashback.clickOnLinksInCashbackPage(translationCashback.sideMenu.history, link),
            withAccountType: true,
            withSusrId: true,
            withUserRole: true,
        });
    }, [pushGtmAnalytic, translationCashback.sideMenu.control]);

    if (!meta.isCashbackAvailable) {
        return <CashbackNotAvailable
            translation={translationCashback.notAvailable}
            urlHowToGetCashback={meta.urlHowToGetCashback}
            onClick={onLinkClick}
        />;
    }

    return (
        <CashbackHistoryStyled>
            <div className="cashback-history__title">
                <CashbackTextStyled className="cashback-history__scores" font={fonts.textHeader5}>
                    {translationCashback.tabHistory.balanceSubtitle}
                    <span data-name='cashbackHistoryBalance'>
                        {formatBalanceWithoutTrailingZeros(meta.balance)} &thinsp;
                        {declination(meta.balance, translationCommon.scores)}
                    </span>
                </CashbackTextStyled>
                {
                    // render drop filter button
                    Object.keys(selectedFilters).length !== 0 && renderDropFilterButton({
                        onClick: () => dropFilterAndSort({}),
                        value: translationCashback.tabHistory.title.dropFilters,
                    })
                }
            </div>

            <CashbackHistoryTable
                accountSelectCurrent={accountSelectCurrent}
                translation={{ ...translationCommon, ...translationCashback.tabHistory }}
            />
        </CashbackHistoryStyled>
    );
};

export interface IConnectedProps extends Pick<IPageCashbackReducer, 'accountSelectCurrent' | 'meta'> {
    translationCashback: IPageCashbackTranslation;
    translationCommon: ICommonTranslation;
}

export interface IDispatchProps {
    pushGtmAnalytic: (payload: IGTMPushAnalyticPayload) => void;
}

export const mapStateToProps = createStructuredSelector<IRootReducer, IConnectedProps>({
    accountSelectCurrent: makeSelectCashbackSelectCurrent(),
    meta: makeSelectCashbackMeta(),
    translationCashback: makeSelectPageCashbackTranslation(),
    translationCommon: makeSelectTranslationCommon(),
});

export const mapDispatchToProps = (dispatch: Dispatch): IDispatchProps => ({
    pushGtmAnalytic: (payload: IGTMPushAnalyticPayload) => dispatch(pushGTMAnalytic(payload)),
});

export default connect(mapStateToProps, mapDispatchToProps)(CashbackHistory);
